import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Plus, Search, Mail, Calendar, Key, RefreshCw, Copy, 
  Eye, SquarePen, Trash2, X, Users, Clock, Phone, MessageCircle, Bell, Send
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const UserManagement = ({ setActiveTab }) => {
  const { refreshUserData } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessNotification(true);
    setTimeout(() => setShowSuccessNotification(false), 3000);
  };
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    phone: '', // WhatsApp do cliente
    validityType: 'days', // 'days' ou 'lifetime'
    validityPeriod: 30 // days
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await adminAPI.getAllUsers();
      
      // Convert date strings to Date objects
      const processedUsers = usersData.map(user => ({
        ...user,
        key_expires_at: new Date(user.key_expires_at),
        created_at: new Date(user.created_at)
      }));
      setUsers(processedUsers);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(user => 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Create user with correct data structure
      await adminAPI.createUser({
        name: newUser.name,
        email: newUser.email,
        phone: newUser.phone, // WhatsApp do cliente
        validity_period: newUser.validityType === 'lifetime' ? 36500 : newUser.validityPeriod, // 100 years for lifetime
        is_lifetime: newUser.validityType === 'lifetime'
      });
      
      setShowCreateModal(false);
      setNewUser({ name: '', email: '', phone: '', validityType: 'days', validityPeriod: 30 });
      await loadUsers();
      showSuccess('✅ Usuário criado e credenciais enviadas via WhatsApp!');
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Erro ao criar usuário. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleRenewKey = async (userId) => {
    if (!confirm('Renovar chave por mais 30 dias?')) return;

    try {
      setLoading(true);
      
      // Get user data before renewal to check if they're currently logged in
      const userToRenew = users.find(u => u.id === userId);
      
      await adminAPI.renewUserKey(userId, 30);
      await loadUsers();
      
      // If this user might be currently logged in, refresh their data
      // This will update their expiration date and remove warning messages
      try {
        await refreshUserData();
      } catch (error) {
        // Silent fail - this just refreshes logged user data if applicable
        console.log('Note: Could not refresh logged user data (user may not be logged in)');
      }
      
      showSuccess('Licença renovada com sucesso.');
    } catch (error) {
      console.error('Error renewing key:', error);
      alert('Erro ao renovar chave. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCredentials = (user) => {
    const isLifetime = user.key_expires_at > new Date(2099, 0, 1);
    const expirationText = isLifetime ? '🔥 VITALÍCIA (Sem prazo de validade)' : `📅 ${user.key_expires_at.toLocaleDateString('pt-BR')}`;
    
    const credentials = `╔═══════════════════════════════════════╗
║   🎯 STAKE MIND - ACESSO PREMIUM    ║
╚═══════════════════════════════════════╝

👤 DADOS DE ACESSO DO CLIENTE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Nome: ${user.name}
Email: ${user.email}
Chave: ${user.access_key}
Validade: ${expirationText}

🌐 PLATAFORMA DE ACESSO
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔗 Link: https://stakemind.com.br/

📱 COMO FAZER LOGIN (Passo a Passo):
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
1️⃣ Acesse: https://stakemind.com.br/
2️⃣ Clique no botão "Usuário"
3️⃣ Digite seu email: ${user.email}
4️⃣ Digite sua chave: ${user.access_key}
5️⃣ Clique em "Entrar" e comece a usar!

✨ RECURSOS DISPONÍVEIS NA PLATAFORMA:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📊 Dashboard - Visão geral da sua performance
💰 Nova Aposta - Registre suas entradas
🏦 Gestão de Banca - Controle suas casas de apostas
📁 Grupos - Organize apostas por estratégias
📈 Histórico Completo - Todas as apostas em um lugar
💸 Despesas - Controle gastos extras (grupos, ferramentas)
📋 Relatórios Avançados - Análises profissionais
🎯 Metas de Lucro - Defina e acompanhe objetivos
📤 Exportação - Baixe dados em PDF/Excel

🛡️ SEGURANÇA E PRIVACIDADE:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
✅ Seus dados são 100% privados e seguros
✅ Sistema com backup automático
✅ Modo privacidade para ocultar valores
${!isLifetime ? '⚠️ Lembre-se de renovar antes de ' + user.key_expires_at.toLocaleDateString('pt-BR') : '♾️ Acesso VITALÍCIO - Nunca expira!'}

📞 SUPORTE E DÚVIDAS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Qualquer dúvida, entre em contato conosco!
Estamos prontos para ajudar você a maximizar seus resultados.

╔═══════════════════════════════════════╗
║  🚀 BEM-VINDO AO STAKE MIND!         ║
║  Transforme suas apostas em negócio  ║
╚═══════════════════════════════════════╝`;

    navigator.clipboard.writeText(credentials);
    alert('✅ Credenciais copiadas com sucesso!\n\nAgora é só colar e enviar para o cliente via WhatsApp, Email ou Telegram.');
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await adminAPI.updateUser(selectedUser.id, {
        name: selectedUser.name,
        email: selectedUser.email,
        phone: selectedUser.phone || '' // Adicionar phone
      });
      
      setShowEditModal(false);
      setSelectedUser(null);
      
      // Forçar reload dos usuários para garantir dados atualizados
      await loadUsers();
      
      showSuccess('Usuário atualizado com sucesso!');
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Erro ao atualizar usuário. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.')) return;

    try {
      setLoading(true);
      await adminAPI.deleteUser(userId);
      await loadUsers();
      showSuccess('Usuário excluído com sucesso!');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Erro ao excluir usuário. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendRenewalNotification = async (user) => {
    // Verificar se tem WhatsApp cadastrado
    if (!user.phone) {
      alert(`❌ Usuário ${user.name} não tem WhatsApp cadastrado.\n\nEdite o usuário e adicione o número do WhatsApp antes de enviar a notificação.`);
      return;
    }

    const confirmed = confirm(
      `📲 Reenviar notificação de renovação?\n\n` +
      `Usuário: ${user.name}\n` +
      `WhatsApp: ${user.phone}\n` +
      `Email: ${user.email}\n\n` +
      `Uma mensagem será enviada incentivando a renovação da assinatura.`
    );
    
    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/resend-renewal-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          user_email: user.email,
          user_name: user.name,
          user_phone: user.phone
        })
      });

      if (!response.ok) {
        throw new Error('Falha ao enviar notificação');
      }

      const data = await response.json();
      showSuccess(`✅ Notificação enviada para ${user.name}!`);
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('❌ Erro ao enviar notificação. Verifique se o WhatsApp está correto.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCredentials = async (user) => {
    // Verificar se tem WhatsApp cadastrado
    if (!user.phone) {
      alert(`❌ Usuário ${user.name} não tem WhatsApp cadastrado.\n\nEdite o usuário e adicione o número do WhatsApp antes de enviar as credenciais.`);
      return;
    }

    const confirmed = confirm(
      `📲 Enviar credenciais de acesso?\n\n` +
      `Usuário: ${user.name}\n` +
      `WhatsApp: ${user.phone}\n` +
      `Email: ${user.email}\n` +
      `Chave: ${user.access_key}\n\n` +
      `Uma mensagem será enviada com os dados de acesso.`
    );
    
    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${user.id}/send-credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Falha ao enviar credenciais');
      }

      const data = await response.json();
      showSuccess(`✅ Credenciais enviadas para ${user.name}!`);
    } catch (error) {
      console.error('Error sending credentials:', error);
      alert(`❌ Erro ao enviar credenciais: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };


  const handleResetUserData = async (userId, userName) => {
    const confirmed = confirm(
      `⚠️ ATENÇÃO: Esta ação irá ZERAR TODOS os dados de ${userName}:\n\n` +
      `• Todas as apostas\n` +
      `• Todas as casas de apostas\n` +
      `• Todos os grupos\n` +
      `• Todas as despesas\n` +
      `• Todas as metas\n` +
      `• Todos os registros diários\n\n` +
      `O usuário poderá continuar usando a conta, mas começará DO ZERO.\n\n` +
      `Tem certeza que deseja continuar?`
    );
    
    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/reset-data`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const deletedSummary = `
🗑️ Dados Zerados com Sucesso!

• ${data.deleted.bets} apostas
• ${data.deleted.betting_houses} casas de apostas
• ${data.deleted.groups} grupos
• ${data.deleted.expenses} despesas
• ${data.deleted.goals} metas
• ${data.deleted.daily_records} registros diários

O usuário ${userName} pode começar do zero agora!
        `;
        alert(deletedSummary);
        showSuccess(`Dados de ${userName} zerados com sucesso!`);
        await loadUsers();
      } else {
        throw new Error('Falha ao zerar dados');
      }
    } catch (error) {
      console.error('Error resetting user data:', error);
      alert('Erro ao zerar dados do usuário. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };


  const handleAdjustDays = async (userId, userName) => {
    const daysInput = prompt(
      `⏰ Ajustar Dias de Assinatura - ${userName}\n\n` +
      `Digite o número de dias para ADICIONAR ou REMOVER:\n\n` +
      `• Número positivo (+30) = Adiciona 30 dias\n` +
      `• Número negativo (-15) = Remove 15 dias\n\n` +
      `Dias a ajustar:`
    );

    if (daysInput === null) return; // Cancelou

    const days = parseInt(daysInput);
    
    if (isNaN(days) || days === 0) {
      alert('❌ Por favor, digite um número válido diferente de zero.');
      return;
    }

    const confirmed = confirm(
      `📅 Confirmar Ajuste de Dias\n\n` +
      `Usuário: ${userName}\n` +
      `Ajuste: ${days > 0 ? '+' : ''}${days} dias\n\n` +
      `${days > 0 ? '✅ Adicionando' : '⚠️ Removendo'} ${Math.abs(days)} dias à assinatura.\n\n` +
      `Confirmar?`
    );

    if (!confirmed) return;

    try {
      setLoading(true);

      // Usar endpoint específico para ajustar dias
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/adjust-days`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          days: days
        })
      });

      if (response.ok) {
        const data = await response.json();
        const message = `
✅ Dias ajustados com sucesso!

Usuário: ${userName}
Ajuste: ${days > 0 ? '+' : ''}${days} dias
Nova data de expiração: ${data.new_expiry}
`;
        alert(message);
        loadUsers();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Falha ao ajustar dias');
      }
    } catch (error) {
      console.error('Erro ao ajustar dias:', error);
      alert(`❌ Erro: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const isUserActive = (user) => {
    return user.key_expires_at > new Date();
  };

  const openDetailsModal = (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const openEditModal = (user) => {
    setSelectedUser({ ...user });
    setShowEditModal(true);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button 
            onClick={() => setActiveTab('admin-dashboard')}
            className="mb-4 flex items-center space-x-2 text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Voltar ao Dashboard</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Gerenciar Usuários
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Crie e gerencie usuários do sistema
          </p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          disabled={loading}
          className="bg-gradient-to-r from-teal-500 to-green-500 text-white px-6 py-3 rounded-lg hover:from-teal-600 hover:to-green-600 transition-colors flex items-center space-x-2 disabled:opacity-50"
        >
          <Plus className="w-5 h-5" />
          <span>Criar Usuário</span>
        </button>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input 
            type="text" 
            placeholder="Buscar por e-mail ou nome..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" 
          />
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Usuários Cadastrados ({filteredUsers.length})
          </h3>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Carregando usuários...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm 
                  ? 'Tente ajustar os termos de busca.'
                  : 'Comece criando o primeiro usuário do sistema.'
                }
              </p>
              {!searchTerm && (
                <button 
                  onClick={() => setShowCreateModal(true)}
                  className="bg-gradient-to-r from-teal-500 to-green-500 text-white px-6 py-3 rounded-lg hover:from-teal-600 hover:to-green-600 transition-colors"
                >
                  Criar Primeiro Usuário
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredUsers.map(user => (
                <div key={user.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  {/* Header - Mobile optimized */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`w-4 h-4 rounded-full mt-1 flex-shrink-0 ${isUserActive(user) ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white truncate">{user.name}</h4>
                          <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
                            isUserActive(user) 
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' 
                              : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                          }`}>
                            {isUserActive(user) ? 'Ativo' : 'Expirado'}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Mail className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{user.email}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 flex-shrink-0" />
                            <span>Desde {formatDate(user.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Key Info - Mobile optimized */}
                  <div className="mb-3 p-3 bg-white dark:bg-gray-600 rounded-lg">
                    <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 mb-1">
                      <Key className="w-4 h-4 flex-shrink-0" />
                      <span className="font-medium">Chave:</span>
                    </div>
                    <p className="text-sm font-mono text-gray-900 dark:text-white break-all">{user.access_key}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Expira em {formatDate(user.key_expires_at)}</p>
                  </div>

                  {/* Actions - Mobile optimized */}
                  <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-2">
                    <button 
                      onClick={() => handleRenewKey(user.id)}
                      className="flex items-center justify-center gap-1 p-2 rounded-lg transition-colors text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20" 
                      title="Renovar por 30 dias"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span className="text-xs hidden sm:inline">Renovar</span>
                    </button>
                    <button 
                      onClick={() => handleCopyCredentials(user)}
                      className="flex items-center justify-center gap-1 p-2 text-teal-600 hover:bg-teal-100 dark:hover:bg-teal-900/20 rounded-lg transition-colors" 
                      title="Copiar credenciais"
                    >
                      <Copy className="w-4 h-4" />
                      <span className="text-xs hidden sm:inline">Copiar</span>
                    </button>
                    <button 
                      onClick={() => openDetailsModal(user)}
                      className="flex items-center justify-center gap-1 p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-lg transition-colors" 
                      title="Ver detalhes"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="text-xs hidden sm:inline">Ver</span>
                    </button>
                    <button 
                      onClick={() => openEditModal(user)}
                      className="flex items-center justify-center gap-1 p-2 text-orange-600 hover:bg-orange-100 dark:hover:bg-orange-900/20 rounded-lg transition-colors" 
                      title="Editar usuário"
                    >
                      <SquarePen className="w-4 h-4" />
                      <span className="text-xs hidden sm:inline">Editar</span>
                    </button>
                    <button 
                      onClick={() => handleAdjustDays(user.id, user.name)}
                      className="flex items-center justify-center gap-1 p-2 text-indigo-600 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 rounded-lg transition-colors" 
                      title="Adicionar ou remover dias de assinatura"
                    >
                      <Clock className="w-4 h-4" />
                      <span className="text-xs hidden sm:inline">+/- Dias</span>
                    </button>
                    <button 
                      onClick={() => handleResendRenewalNotification(user)}
                      className="flex items-center justify-center gap-1 p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/20 rounded-lg transition-colors" 
                      title="Reenviar notificação de renovação via WhatsApp"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-xs hidden sm:inline">Renovação</span>
                    </button>
                    <button 
                      onClick={() => handleSendCredentials(user)}
                      className="flex items-center justify-center gap-1 p-2 text-cyan-600 hover:bg-cyan-100 dark:hover:bg-cyan-900/20 rounded-lg transition-colors" 
                      title="Enviar credenciais de acesso via WhatsApp"
                    >
                      <Send className="w-4 h-4" />
                      <span className="text-xs hidden sm:inline">Credenciais</span>
                    </button>
                    <button 
                      onClick={() => handleResetUserData(user.id, user.name)}
                      className="flex items-center justify-center gap-1 p-2 text-purple-600 hover:bg-purple-100 dark:hover:bg-purple-900/20 rounded-lg transition-colors" 
                      title="Zerar todos os dados (começar do zero)"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span className="text-xs hidden sm:inline">Zerar</span>
                    </button>
                    <button 
                      onClick={() => handleDeleteUser(user.id)}
                      className="flex items-center justify-center gap-1 p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors" 
                      title="Excluir usuário"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="text-xs hidden sm:inline">Excluir</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Criar Novo Usuário
              </h3>
            </div>
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  required
                  value={newUser.name}
                  onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="João da Silva"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  E-mail *
                </label>
                <input
                  type="email"
                  required
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="joao@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  WhatsApp (com DDD) *
                </label>
                <input
                  type="tel"
                  required
                  value={newUser.phone}
                  onChange={(e) => setNewUser(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="5511916560595"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  📱 Formato: 55 + DDD + Número (ex: 5511916560595)
                </p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  ✅ As credenciais serão enviadas automaticamente via WhatsApp!
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tipo de Validade *
                </label>
                <select
                  value={newUser.validityType}
                  onChange={(e) => setNewUser(prev => ({ ...prev, validityType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="days">Prazo Limitado</option>
                  <option value="lifetime">Vitalícia</option>
                </select>
              </div>

              {newUser.validityType === 'days' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Duração *
                  </label>
                  <select
                    value={newUser.validityPeriod}
                    onChange={(e) => setNewUser(prev => ({ ...prev, validityPeriod: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value={1}>24 Horas (Teste)</option>
                    <option value={30}>30 Dias</option>
                  </select>
                </div>
              )}

              {newUser.validityType === 'lifetime' && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    ⚠️ Chave vitalícia: O usuário terá acesso permanente ao sistema.
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-gradient-to-r from-teal-500 to-green-500 text-white rounded-lg hover:from-teal-600 hover:to-green-600 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Criando...' : 'Criar Usuário'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Detalhes do Usuário
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Nome</label>
                <p className="text-gray-900 dark:text-white font-medium">{selectedUser.name}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">E-mail</label>
                <p className="text-gray-900 dark:text-white font-medium">{selectedUser.email}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Chave de Acesso</label>
                <p className="text-gray-900 dark:text-white font-mono text-sm bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded">
                  {selectedUser.access_key}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
                <span className={`inline-block px-3 py-1 text-sm rounded-full ${
                  isUserActive(selectedUser) 
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' 
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                }`}>
                  {isUserActive(selectedUser) ? 'Ativo' : 'Expirado'}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Data de Criação</label>
                <p className="text-gray-900 dark:text-white font-medium">{formatDate(selectedUser.created_at)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Data de Expiração</label>
                <p className="text-gray-900 dark:text-white font-medium">{formatDate(selectedUser.key_expires_at)}</p>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Editar Usuário
              </h3>
            </div>
            <form onSubmit={handleEditUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  required
                  value={selectedUser.name}
                  onChange={(e) => setSelectedUser(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  E-mail *
                </label>
                <input
                  type="email"
                  required
                  value={selectedUser.email}
                  onChange={(e) => setSelectedUser(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  WhatsApp (com DDD)
                </label>
                <input
                  type="tel"
                  placeholder="11999999999"
                  value={selectedUser.phone || ''}
                  onChange={(e) => setSelectedUser(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Exemplo: 5511999999999 (código do país + DDD + número)
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Success Notification */}
      {showSuccessNotification && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-2 z-50 animate-bounce">
          <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
            <span className="text-green-500 text-sm font-bold">✓</span>
          </div>
          <span className="font-medium">
            {successMessage}
          </span>
        </div>
      )}
    </div>
  );
};

export default UserManagement;